import React from 'react';
import NextApp, { AppContext } from 'next/app';
import Head from 'next/head';
import { Provider } from 'react-redux';
import store from 'data/store/store';
import { GlobalStyles } from 'styles/GlobalStyles';
import {
  circularSpotifyUiLatinOs2V3BookWoff2,
  circularSpotifyUiLatinOs2V3BlackWoff2,
  circularSpotifyUiLatinOs2V3BoldWoff2,
} from '@spotify-internal/encore-web';

import { guessPlanType, PlanType } from 'lib/plans';
import { DuoProvider, FamilyProvider } from 'data/plan/withPlan';
import {
  getMainLanguage,
  getTranslationFunction,
  getTranslations,
  isRTLLanguage,
  setLocale,
  Translations,
} from 'lib/i18n';
import { TranslationContext } from 'data/translation/context';
import { Locale } from 'lib/marketLocales';
import { setupReporters } from 'lib/metrics';
import { logPageView, withRouteMetrics } from 'lib/semanticMetrics';
import '@spotify-internal/masthead-react/index.css';
import { track } from '@spotify-internal/track-js';
import { StyleSheetManager } from 'styled-components';
import RTLPlugin from 'styles/rtl-plugin';
import { isRunningInCypress } from 'lib/env';
import { NextRouter } from 'next/router';
import {
  getIframeHostEnvironmentFromQueryParam,
  IframeHostEnvironment,
} from 'lib/iframe';

function getPlanProvider(planType: PlanType) {
  if (planType === 'duo') {
    return DuoProvider;
  }
  return FamilyProvider;
}

export type CommonPageProps = {
  translations: Translations;
  locale: Locale;
  isLoaded: boolean;
  iframeHostEnvironment: IframeHostEnvironment;
};

class CustomApp extends NextApp<{
  translations: Translations;
  locale: Locale;
  iframeHostEnvironment: IframeHostEnvironment;
}> {
  state = {
    isLoaded: false,
  };
  router: NextRouter | null = null;

  static async getInitialProps(appContext: AppContext) {
    const locale = appContext.router.locale as Locale;
    const translations = await getTranslations(locale || 'us');
    const appProps = await NextApp.getInitialProps(appContext);

    return {
      ...appProps,
      locale,
      translations,
      iframeHostEnvironment: getIframeHostEnvironmentFromQueryParam(
        appContext.router.query.e,
      ),
    };
  }
  handleRouteChange = () => {
    if (this.router !== null) {
      logPageView({
        route: this.router.pathname,
      });
    }
  };

  componentDidMount() {
    const { router, locale, translations, iframeHostEnvironment } = this.props;
    this.router = router;
    const isNotRunningInTest = !isRunningInCypress();

    const consentBoxEnabled = iframeHostEnvironment === null;
    setLocale(locale, () => Promise.resolve(translations)).then(() =>
      this.setState({ isLoaded: true }),
    );

    if (window !== undefined) {
      setupReporters({
        pathName: router.pathname,
        locale,
      });
      this.handleRouteChange();
    }

    if (isNotRunningInTest && consentBoxEnabled) {
      track({
        dataLayerPushInitial: true,
        googleTagManagerId: 'GTM-7BJJ',
        language: getMainLanguage(locale),
        market: locale,
        oneTrust: true,
      });
      router.events.on('routeChangeComplete', this.handleRouteChange);
    }
  }

  componentWillUnmount() {
    const { router } = this.props;
    router.events.off('routeChangeComplete', this.handleRouteChange);
  }

  render() {
    const { Component, pageProps, router, locale, iframeHostEnvironment } =
      this.props;
    const PlanProvider = getPlanProvider(guessPlanType(router.asPath));
    const { isLoaded } = this.state;
    const stylisPlugins = [...(isRTLLanguage(locale) ? [RTLPlugin] : [])];
    const t = getTranslationFunction(() => this.props.translations);

    const pathname = router?.pathname ?? '';
    const isRunningInIframe =
      iframeHostEnvironment !== null && pathname.includes('home-hub');

    return (
      <>
        <Head>
          <title>Spotify</title>
          <meta name="googlebot" content="noindex" />
          <link rel="icon" href="https://www.scdn.co/i/_global/favicon.png" />
          <link
            rel="apple-touch-icon-precomposed"
            sizes="144x144"
            href="https://www.scdn.co/i/_global/touch-icon-144.png"
          />
          <link
            rel="apple-touch-icon-precomposed"
            sizes="114x114"
            href="https://www.scdn.co/i/_global/touch-icon-114.png"
          />
          <link
            rel="apple-touch-icon-precomposed"
            sizes="72x72"
            href="https://www.scdn.co/i/_global/touch-icon-72.png"
          />
          <link
            rel="apple-touch-icon-precomposed"
            href="https://www.scdn.co/i/_global/touch-icon-57.png"
          />

          <link
            rel="preload"
            href={circularSpotifyUiLatinOs2V3BookWoff2}
            as="font"
            type="font/woff2"
            crossOrigin="true"
          />
          <link
            rel="preload"
            href={circularSpotifyUiLatinOs2V3BlackWoff2}
            as="font"
            type="font/woff2"
            crossOrigin="true"
          />
          <link
            rel="preload"
            href={circularSpotifyUiLatinOs2V3BoldWoff2}
            as="font"
            type="font/woff2"
            crossOrigin="true"
          />
          {isRunningInIframe && <base target="_top" />}
        </Head>
        <GlobalStyles />
        <StyleSheetManager stylisPlugins={stylisPlugins}>
          <Provider store={store}>
            <TranslationContext.Provider value={{ locale, t }}>
              <PlanProvider>
                <Component
                  isLoaded={isLoaded}
                  {...pageProps}
                  locale={locale}
                  iframeHostEnvironment={iframeHostEnvironment}
                />
              </PlanProvider>
            </TranslationContext.Provider>
          </Provider>
        </StyleSheetManager>
      </>
    );
  }
}
// @ts-ignore next-router-metrics expects NextApp with no custom props
export default withRouteMetrics(CustomApp);
